import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningIcon from '@mui/icons-material/Warning';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import {
  Accordion,
  AccordionSummary,
  Autocomplete,
  Button,
  Chip,
  Divider,
  IconButton,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useMutation,useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useContext,useEffect,useMemo,useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEye,FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import { AuthContext } from "../../context/AuthContext";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import PlanoDeAcao from "../../service/PlanoDeAcao";
import queryClient from "../../service/query";
import { logPlanoType,planoDeAcaoType,planosResponseType,propsModalType,taskType } from "../../types/plano-de-acao/types";
import ImagesCmp from "../ImagesCmp";
import usePagination from "../Pagination/Pagination";
import ModalEditaStatus from "./modal/EditaStatus";
import ModalLogs from "./modal/Logs";
import ModalFinalizaPA from "./modal/ModalFinalizaPA";
require('moment/locale/pt-br');

const planosService = new PlanoDeAcao();

export default function PlanosDeAcaoComp() {
  const { userData } = useContext(UserContext);
  const { session } = useContext(AuthContext);
  const [open, setOpen] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [openLogs, setOpenLogs] = useState<boolean>(false);
  const [propsModal, setPropsModal] = useState<propsModalType>({} as propsModalType);
  const [openFinaliza, setOpenFinaliza] = useState<boolean>(false);
  const [planoId, setPlanoId] = useState<string>('');
  const [loadingRetoma, setLoadingRetoma] = useState(false);
  const [showDetails, setShowDetails] = useState<number | null>(null);
  const { t } = useTranslation('translation');
  const [logs, setLogs] = useState<logPlanoType[]>([
    {
      acao: '',
      user: '',
      data: 0,
      foto: '',
    }
  ]);

  function handleOpenLogs() {
    setOpenLogs(!openLogs);
  }

  const handleChange = (isExpanded: boolean, panel: string) => {
    setExpanded(isExpanded ? panel : false);
  };

  const userId = session?.userId ?? '';

  const { data, isLoading, isFetching, isError, isSuccess } = useQuery({
    queryKey: ['GET_PLANOS_ACAO'],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const response: planosResponseType = await planosService.getPlansByUser(token);
        return response;
      }
    },
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    refetchInterval: 5 * 60 * 1000
  });

  useMemo(() => {
    if (data && !data?.planos?.length) {
      toast.error(`${t('ActionPlan.PlanosDeAcaoComp.noTasksOrActionPlans')}`);
    }
  }, [data, t]);

  const { mutate, isPending } = useMutation({
    mutationKey: ['FINALIZA_PLANO'],
    mutationFn: async (value: string) => {
      const promise = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const response = await planosService.finalizaPlano(token, value);
          if (!response) {
            throw new Error('Error finalizing plan');
          }
          return response;
        }
      }, {
        pending: `${t('ActionPlan.PlanosDeAcaoComp.finishingPlan')}`,
        success: `${t('toast.planCompleted')}`,
        error: `${t('ActionPlan.PlanosDeAcaoComp.errorFinishingPlan')}`
      });
      return promise;
    },
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ['GET_PLANOS_ACAO'] });
    },
  });

  const onLoadding = isLoading || isFetching || isPending;

  useEffect(() => {
    if (isSuccess) trackEventMatomo('plano de ação', 'sucesso', 'carregamento', 'planos de ação carregados');
    if (data?.planos?.length && isSuccess) {
      toast.success(`${data?.planos.length} Planos planos de ação`, { toastId: 'successoFinalizado' });
    }
  }, [data, isSuccess, t]);

  useEffect(() => {
    if (isError) {
      trackEventMatomo('plano de ação', 'erro', 'carregamento', 'falha ao carregar planos de ação');
      toast.error('Erro ao buscar planos de ação', { toastId: 'erro' });
    }
  }, [data, onLoadding, isError, t]);

  const PER_PAGE = 5;

  const sortedHosts = data?.planos
    .filter((plano: planoDeAcaoType) => plano.nome.toLowerCase().includes(search.toLowerCase()))
    ?.sort((a: planoDeAcaoType, b: planoDeAcaoType) =>
      a.dataCriacao && b.dataCriacao && a.dataCriacao < b.dataCriacao ? 1 : -1
    );

  const count = Math.ceil((data?.planos?.length || 0) / PER_PAGE);

  const _DATA = usePagination<planoDeAcaoType>(sortedHosts as planoDeAcaoType[], PER_PAGE);

  const [page, setPage] = useState(1);

  const handleChangePage = (_e: React.ChangeEvent<unknown>, p: number) => {
    trackEventMatomo('plano de ação', 'click', 'paginação', `acessa página ${p}`);
    setPage(p);
    _DATA.jump(p);
  };

  function Loading() {
    return (
      <>
        <Skeleton height={30} width='300px' variant="rounded" />
        {Array(5).fill(0).map((_, index) => (
          <Skeleton key={index} height={150} width='100%' variant="rounded" />
        ))}
      </>
    );
  };

  const getStatus = (value: string) => {
    switch (value) {
      case 'aberto':
        return (
          <span className="tag is-success is-small"  >{t('ActionPlan.PlanosDeAcaoComp.open')}</span>
        );
      case 'fecahdo':
        return (
          <span className="tag is-danger is-small">{t('ActionPlan.PlanosDeAcaoComp.closed')}</span>
        );
      case 'aguardandoAnalise':
        return (
          <span className="tag is-info is-small">{t('ActionPlan.PlanosDeAcaoComp.awaitingAnalysis')}</span>
        );
      default: return <></>;
    }
  };

  const getTaskStatus = (value: string) => {
    switch (value) {
      case 'pendente':
        return (
          <span style={{ display: 'flex', width: '100%' }} className="tag is-warning is-small">{t('ActionPlan.PlanosDeAcaoComp.pending')}</span>
        );
      case 'emExecucao':
        return (
          <span style={{ display: 'flex', width: '100%' }} className="tag is-small is-info">{t('ActionPlan.PlanosDeAcaoComp.inExecution')}</span>
        );
      case 'concluido':
        return (
          <span style={{ display: 'flex', width: '100%' }} className="tag is-small is-primary">{t('ActionPlan.PlanosDeAcaoComp.completed')}</span>
        );
      case 'aprovado':
        return (
          <span style={{ display: 'flex', width: '100%' }} className="tag is-small is-success">{t('ActionPlan.PlanosDeAcaoComp.approved')}</span>
        );
      case 'rejeitado':
        return (
          <span style={{ display: 'flex', width: '100%' }} className="tag is-small is-danger">{t('ActionPlan.PlanosDeAcaoComp.rejected')}</span>
        );
      default: return <></>;
    }
  };

  const calculatePercent = (plano: planoDeAcaoType) => {
    if (plano.tasks) {
      const totalTasks = plano.tasks.length;
      let count = 0;
      plano.tasks.forEach(task => {
        if (task.status === 'aprovado' || task.status === 'concluido') {
          count += 1;
        }
      });
      return count ? ((totalTasks / count) * 100).toFixed(2) : count;
    }
  };

  const verifyDisableBtn = (responsavelPlano: string, responsavelTask: string, status: "pendente" | "emExecucao" | "concluido" | "aprovado" | "rejeitado") => {
    switch (status) {
      case 'pendente': case 'emExecucao': case 'rejeitado':
        return responsavelTask !== userId;
      case 'concluido':
        return responsavelPlano !== userId;
      case 'aprovado':
        return true;
      default:
        return false;
    }
  };

  const verifyConclude = (plano: planoDeAcaoType) => {
    return plano.tasks.every((task: taskType) => task.status === 'aprovado');
  };

  const countTasksStatus = (plano: planoDeAcaoType, status: string) => {
    let count = 0;
    plano.tasks.forEach(task => {
      if (task.status === status && task.responsavel === userId) count += 1;
    });
    return count;
  };

  const countTasksExpired = (plano: planoDeAcaoType) => {
    let count = 0;
    plano.tasks.forEach(task => {
      if (Date.now() > task.dataLimite) count += 1;
    });
    return count;
  };

  const calculateTime = (limit: number) => {
    const now = moment();
    const end = moment(limit);

    if (now.isAfter(end)) {
      const dias = now.diff(end, 'days');

      return `${t('ActionPlan.PlanosDeAcaoComp.ago')} ${dias} ${dias <= 1
        ? t('ActionPlan.PlanosDeAcaoComp.day')
        : t('ActionPlan.PlanosDeAcaoComp.days')
        }`;
    }

    return moment(limit).locale('pt-br').endOf('day').fromNow();
  };

  const getTagExpirationLabel = (expiration: number) => {
    if (Date.now() > expiration) {
      const now = moment(new Date());
      const limit = moment(new Date(expiration));
      const diff = Number(moment(now.diff(limit, 'milliseconds')).format('D'));
      if (diff === 0) {
        return 'info';
      } else if (diff > 0 && diff < 5) {
        return 'warning';
      } else if (diff >= 5) {
        return 'danger';
      } else {
        return 'success';
      }
    } else {
      const difference = expiration - Date.now();
      const resto = difference % 86400000;
      const diff = Number(moment(difference - resto).format('D'));
      if (diff <= 2 || difference < 86400000) {
        return 'warning';
      } else {
        return 'info';
      }
    }
  };

  if (data && !onLoadding && !data.planos?.length) {
    return (
      <Typography variant="h4" color='textDisabled' fontWeight={600}>
        {t('ActionPlan.PlanosDeAcaoComp.noTasksOrActionPlans')}
      </Typography>
    );
  }

  return (
    <>
      {openFinaliza
        && <ModalFinalizaPA
          openFinaliza={openFinaliza}
          setOpenFinaliza={setOpenFinaliza}
          mutate={mutate}
          planoId={planoId}
        />}
      {open
        && <ModalEditaStatus
          propsModal={propsModal}
          open={open}
          setOpen={setOpen}
        />}
      {openLogs
        && <ModalLogs
          logs={logs}
          openLogs={openLogs}
          handleOpenLogs={handleOpenLogs}
        />}
      <Stack
        direction='column'
        width='100%'
        justifyContent='flex-start'
        alignItems='flex-start'
        spacing={2}
      >
        {onLoadding
          ? (<Loading />) :
          <Stack
            direction="column"
            spacing={1}
            justifyContent="flex-start"
            alignItems="stretch"
            width='100%'
          >
            <>
              <Stack
                justifyContent='flex-start'
                alignItems='flex-start'
                width='100%'
                direction="column"
                spacing={1}
              >
                {data && data?.planos?.length > 5
                  && <>
                    <Pagination
                      boundaryCount={4}
                      showFirstButton
                      showLastButton
                      shape="rounded"
                      count={count}
                      page={page}
                      onChange={(eve: React.ChangeEvent<unknown>, pageNumber: number) => {
                        trackEventMatomo('Comp PA', 'click', 'button', 'muda página');
                        handleChangePage(eve, pageNumber);
                      }}
                    />
                    <Autocomplete
                      fullWidth
                      loading={onLoadding}
                      value={search ? ({ nome: search } as planoDeAcaoType) : null}
                      getOptionLabel={option => option.nome as string}
                      getOptionKey={option => option?.id as string}
                      isOptionEqualToValue={(option, value) => option.nome === value.nome}
                      options={data?.planos
                        ?.sort((a, b) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
                        ?.map((ele: planoDeAcaoType) => ({ ...ele })) || []}
                      onChange={(_eve, value: planoDeAcaoType | null, reason) => {
                        if (value?.nome) {
                          setSearch(value?.nome as string);
                          handleChangePage(_eve, 1);
                        }
                        else {
                          setSearch("");
                        }
                      }}
                      renderInput={(params) => <TextField {...params} label={t('ActionPlan.PlanosDeAcaoComp.searchPlanByName')} />}
                    />
                  </>}
              </Stack>
              {data?.planos && data?.planos?.length > 0
                && _DATA.currentData()?.map((plano: planoDeAcaoType) =>
                  <Accordion key={plano.dataCriacao} expanded={expanded === plano.id} sx={{ width: '100%', padding: 1 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon
                        onClick={(isExpanded) => {
                          trackEventMatomo('Comp PA', 'click', 'acordeon', 'abre/fecha modal');
                          if (expanded === plano.id) {
                            handleChange(!isExpanded, '');
                          } else handleChange(true, String(plano.id));
                        }}
                      />}>
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent='space-between'
                        alignItems='flex-start'
                        width='100%'
                        padding={1}
                      >
                        <Stack
                          width='100%'
                          direction="column"
                          spacing={1}
                          justifyContent='flex-start'
                          alignItems='flex-start'
                        >
                          <Typography variant='h5' fontWeight={600} sx={{ color: '#47AA9C', wordBreak: 'break-word' }}>
                            {plano.nome}
                          </Typography>
                          {plano.dataEdicao && (
                            <Typography color='textDisabled' fontSize='small'>
                              {`${t('ActionPlan.PlanosDeAcaoComp.edited')} - ${new Date(plano.dataEdicao).toLocaleString('pt-BR')}`}
                            </Typography>
                          )}
                          {plano.dataReinicio && (
                            <Typography color='textDisabled' fontSize='small'>
                              {`${t('ActionPlan.PlanosDeAcaoComp.reopened')} - ${new Date(plano.dataReinicio).toLocaleString('pt-BR')}`}
                            </Typography>
                          )}
                          <Typography color='textDisabled' fontSize='small' sx={{ wordBreak: 'break-all' }}>
                            {t('ActionPlan.PlanosDeAcaoComp.createdBy')}&nbsp;
                            <b>{plano.criadorNome ? plano.criadorNome : '-'}</b>&nbsp;
                            {t('ActionPlan.PlanosDeAcaoComp.on')}&nbsp;
                            {new Date(Number(plano?.dataCriacao))?.toLocaleString('pt-BR')}&nbsp;
                            {t('ActionPlan.PlanosDeAcaoComp.fromChecklist')} <b>{plano.formularioNome}</b>
                          </Typography>
                          <Typography color='textDisabled' fontSize='small'>
                            ({t('ActionPlan.PlanosDeAcaoComp.formExecutedBy')}&nbsp;
                            {plano?.idExecucao?.split('|')[0]}&nbsp;
                            {t('ActionPlan.PlanosDeAcaoComp.on')}&nbsp;
                            {new Date(Number(plano.idExecucao?.split('|')[1]))?.toLocaleString('pt-BR')})
                            | {t('ActionPlan.PlanosDeAcaoComp.start')} -
                            <b>{new Date(Number(plano.idExecucao?.split('|')[1]))?.toLocaleString('pt-BR')}</b>
                          </Typography>
                          <Typography color='textDisabled' fontSize='small'>
                            {t('ActionPlan.PlanosDeAcaoComp.planResponsible')}&nbsp;
                            <b>{plano.responsavelNome ? plano.responsavelNome
                              : plano.responsavel}</b>
                          </Typography>
                          <Typography color='textDisabled' fontSize='small'>
                            {t('ActionPlan.PlanosDeAcaoComp.planDeadline')}&nbsp;
                            <b>{new Date(plano.dataLimite).toLocaleString('pt-BR')}</b>
                          </Typography>
                        </Stack>
                        <Stack
                          direction="column"
                          spacing={1}
                          justifyContent='flex-start'
                          alignItems='stretch'
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                            alignItems="center"
                          >
                            {plano.tasks?.some(task => task.responsavel === userId && task.status === 'pendente') &&
                              <Tooltip title={`${countTasksStatus(plano, 'pendente')} ${countTasksStatus(plano, 'pendente') <= 1 ? `${t('ActionPlan.PlanosDeAcaoComp.pendingTask')}` : `${t('ActionPlan.PlanosDeAcaoComp.pendingTasks')}`}`}>
                                <WarningIcon color='warning' />
                              </Tooltip>
                            }
                            {plano.tasks.some(task => task.responsavel === userId && task.status === 'rejeitado') &&
                              <Tooltip title={`${countTasksStatus(plano, 'rejeitado')} ${countTasksStatus(plano, 'rejeitado') <= 1 ? `${t('ActionPlan.PlanosDeAcaoComp.rejectedTask')}` : `${t('ActionPlan.PlanosDeAcaoComp.rejectedTasks')}`}`}>
                                <ErrorIcon color="error" />
                              </Tooltip>
                            }
                            {plano.tasks.some(task => Date.now() > task.dataLimite) &&
                              <Tooltip title={`${countTasksExpired(plano)} ${countTasksExpired(plano) <= 1 ? `${t('ActionPlan.PlanosDeAcaoComp.expiredTask')}` : `${t('ActionPlan.PlanosDeAcaoComp.expiredTasks')}`}`}>
                                <WatchLaterIcon color='secondary' />
                              </Tooltip>
                            }
                          </Stack>
                          {verifyConclude(plano)
                            && <Button
                              endIcon={<CheckCircleIcon />}
                              onClick={() => {
                                trackEventMatomo('Comp PA', 'click', 'button', 'finaliza PA');
                                setOpenFinaliza(!openFinaliza);
                                setPlanoId(String(plano.id));
                              }}
                              className="button is-small is-info">
                              {t('ActionPlan.PlanosDeAcaoComp.finishPlan')}
                            </Button>}
                          {getStatus(plano?.status)}
                          {Date.now() > plano.dataLimite
                            && <span className={`tag is-small is-${getTagExpirationLabel(plano.dataLimite)}`}>
                              {`${t('ActionPlan.PlanosDeAcaoComp.expiredPlan')} ${calculateTime(plano.dataLimite)}`}
                            </span>}
                          {Date.now() < plano.dataLimite
                            && <span className={`tag is-small is-${getTagExpirationLabel(plano.dataLimite)}`}>
                              {`${t('ActionPlan.PlanosDeAcaoComp.planExpires')} ${calculateTime(plano.dataLimite)}`}
                            </span>}
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <Divider variant="middle" />
                    <Stack width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={2} direction='column' padding={2}>
                      <Stack direction='column' spacing={1} justifyContent='flex-start' alignItems='flex-start' width='100%'>
                        <Typography fontWeight={600} color='textDisabled'>
                          {t('ActionPlan.PlanosDeAcaoComp.nonConformitiesOfThisPlan')}
                        </Typography>
                        {plano?.naoConformidades?.map((naoConf, i) => (
                          <>
                            <Stack direction='row' width='100%' justifyContent='flex-start' alignItems='center' spacing={2}>
                              <IconButton
                                onClick={() => {
                                  trackEventMatomo('Comp PA', 'click', 'button', 'exibe detalhes PA');
                                  setShowDetails((prev) => prev !== null && prev === i ? null : i);
                                }}
                              >
                                {showDetails !== null && showDetails === i ? <FaEyeSlash /> : <FaEye />}
                              </IconButton>
                              <Typography color='textDisabled'>
                                {naoConf.nome}
                              </Typography>
                            </Stack>
                            {showDetails !== null && showDetails === i && (
                              <Stack direction='column' alignItems='flex-start' justifyContent='flex-start' spacing={1} width='100%' paddingLeft={6.5}>
                                <Typography variant="body2" color='textDisabled'>
                                  {t('ActionPlan.PlanosDeAcaoComp.evaluation')}
                                </Typography>
                                <Stack direction='column' alignItems='flex-start' justifyContent='flex-start' spacing={1} width='100%' paddingLeft={2}>
                                  {naoConf.avaliacao && naoConf.avaliacao.map(ava => (
                                    <Typography fontWeight={`${naoConf.nota === ava.valor ? 'bold' : '100'}`} fontSize={13}>{ava.nome}</Typography>
                                  ))}
                                </Stack>
                                <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' spacing={1}>
                                  {naoConf.ocorrencia
                                    && <Typography
                                      fontWeight={650}
                                      fontSize={13}
                                      color='textDisabled'
                                      component={'span'}>
                                      {t('ActionPlan.PlanosDeAcaoComp.comment')} <Typography fontSize={13} sx={{ wordBreak: 'break-word' }}>{naoConf.ocorrencia}</Typography>
                                    </Typography>}
                                  {naoConf.img && <ImagesCmp images={naoConf.img} />}
                                </Stack>
                              </Stack>
                            )}
                          </>
                        ))}
                      </Stack>
                      <Stack width='100%' direction='column' spacing={1}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Typography fontWeight={600} fontSize={14}>
                            {t('ActionPlan.PlanosDeAcaoComp.tasks')}
                          </Typography>
                          <Chip size="small" color="info" sx={{ fontWeight: 600 }} label={`${calculatePercent(plano)}%`} />
                        </Stack>
                        <Divider variant="middle" />
                        <ol>
                          {plano.tasks?.map((task, taskIndex) =>
                            <>
                              <Stack
                                padding={1.5}
                                key={task.fim}
                                direction="row"
                                spacing={1}
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  justifyContent="flex-start"
                                  alignItems="center"
                                >
                                  {task.log
                                    && <Tooltip title={task.log
                                      ? `${t('ActionPlan.PlanosDeAcaoComp.viewLogs')}`
                                      : `${t('ActionPlan.PlanosDeAcaoComp.noLogsAvailable')}`}>
                                      <IconButton
                                        onClick={() => {
                                          trackEventMatomo('Comp PA', 'click', 'button', 'abre modal');
                                          setLogs([...task.log as logPlanoType[]]);
                                          setOpenLogs(!openLogs);
                                        }}
                                        disabled={!task.log}
                                      >
                                        <FaEye />
                                      </IconButton>
                                    </Tooltip>}
                                  <Divider variant='middle' orientation="vertical" />
                                  <Stack
                                    direction="column"
                                    spacing={1}
                                    justifyContent="flex-start"
                                    alignItems="flex-start"
                                  >
                                    <Typography fontSize={14} fontWeight={600} color='textDisabled' component={'li'}>{task.nome}</Typography>
                                    <Typography fontSize={12} color='textDisabled'>
                                      ({t('ActionPlan.PlanosDeAcaoComp.responsible')}:&nbsp;
                                      <b>{task.responsavelNome
                                        ? task.responsavelNome
                                        : task.responsavel}
                                      </b>)
                                    </Typography>
                                    {task.detalhes
                                      && <>
                                        <Typography fontWeight={600} color='textDisabled' fontSize={12}>{t('ActionPlan.PlanosDeAcaoComp.details')}</Typography>
                                        <Typography sx={{ wordBreak: 'break-word' }} fontSize={13} >{task.detalhes}</Typography>
                                      </>}
                                  </Stack>
                                </Stack>
                                <Stack
                                  direction="column"
                                  spacing={1}
                                  justifyContent="center"
                                  alignItems='stretch'
                                >
                                  <Stack direction='row' width='100%' alignItems='center'>
                                    <Typography fontSize={12} fontWeight={600} color='textDisabled' component='span'>
                                      {t('ActionPlan.PlanosDeAcaoComp.status')}
                                    </Typography>&nbsp;
                                    {getTaskStatus(task.status)}
                                  </Stack>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="flex-start"
                                    alignItems="center"
                                  >
                                    {task.responsavel === userId
                                      && task.status === 'rejeitado'
                                      ? <Button
                                        size="small"
                                        variant='contained'
                                        endIcon={<RestartAltIcon />}
                                        disabled={loadingRetoma}
                                        onClick={async () => {
                                          trackEventMatomo('Comp PA', 'click', 'button', 'retoma tarefa');
                                          setLoadingRetoma(true);
                                          const token = await AttToken();
                                          if (token) {
                                            await planosService.changeStatusTask(token, { taskIndex, status: 'emExecucao', planoId: plano.id as string });
                                            queryClient.resetQueries({ queryKey: ['GET_PLANOS_ACAO'] });
                                          }
                                          setLoadingRetoma(false);
                                        }}
                                      >
                                        {'Retomar tarefa'}
                                      </Button>
                                      : <Button
                                        variant='contained'
                                        endIcon={<ModeEditIcon />}
                                        size="small"
                                        disabled={verifyDisableBtn(plano.responsavel, task.responsavel, task.status)}
                                        onClick={() => {
                                          trackEventMatomo('Comp PA', 'click', 'button', 'edita status');
                                          setOpen(!open);
                                          setPropsModal({
                                            planoId: String(plano.id),
                                            taskIndex,
                                            logado: userId,
                                            responsavel: task.responsavel,
                                            criador: plano.responsavel,
                                            statusTask: task?.status,
                                            obrigatorioEvidencia: task?.obrigatorioEvidencia,
                                            responsavelNome: plano?.responsavelNome,
                                          });
                                        }}
                                      >
                                        {verifyDisableBtn(plano.responsavel, task.responsavel, task.status)
                                          ? `${t('ActionPlan.PlanosDeAcaoComp.editionBlocked')}`
                                          : `${plano?.responsavel === userData.id ? 'Avaliar tarefa' : t('ActionPlan.PlanosDeAcaoComp.editStatus')}`}
                                      </Button>}
                                  </Stack>
                                  <Stack direction='row' width='100%' alignItems='center'>
                                    <Typography fontSize={12} fontWeight={600} color='textDisabled'>
                                      {t('Reports.ticketList.deadline')}&nbsp;
                                    </Typography>
                                    <span style={{ display: 'flex', width: '100%' }} className={`tag is-${getTagExpirationLabel(task.dataLimite)}`}>
                                      {new Date(task.dataLimite).toLocaleDateString('pt-BR')}
                                    </span>
                                  </Stack>
                                  {Date.now() > task.dataLimite
                                    && <span className={`tag is-small is-${getTagExpirationLabel(task.dataLimite)}`}>
                                      {`${t('ActionPlan.PlanosDeAcaoComp.expiredTask')} 
                                      ${calculateTime(task.dataLimite)}
                                      `}
                                    </span>}
                                  {Date.now() <= task.dataLimite
                                    && <span className={`tag is-small is-${getTagExpirationLabel(task.dataLimite)}`}>
                                      {`${t('ActionPlan.PlanosDeAcaoComp.expiredTask')}
                                       ${calculateTime(task.dataLimite)}
                                       `}
                                    </span>}
                                </Stack>
                              </Stack>
                              <Divider key={taskIndex} variant='middle' flexItem />
                            </>
                          )}
                        </ol>
                      </Stack>
                    </Stack>
                  </Accordion>
                )}
            </>
          </Stack>
        }
      </Stack>
    </>
  );
}
